/**
 * THE ROLLOVER CHAIN AUTHORITY — the one way future chain work is re-planned.
 *
 * Every path that changes when an existing chain's unresolved work can happen
 * — a pushback, a job change, a run-long, a lane cascade, an AutoBump, the
 * parking-lot cron, an operator's date change — asks THIS module, from the
 * browser (the app's client) or from an Edge function (its own client):
 *
 *     frozen history ─ unresolved tail ─ pool of future work ─ earliest valid
 *     start ─ ONE planRolloverSegments ─ ONE replan_rollover_chain_from_source
 *
 * Nobody else decides a continuation's date, start, finish, book share,
 * OptiTime, or how many days the chain has. A caller may observe a chain, or
 * ask for a re-plan from a boundary; it may not re-time a segment itself.
 *
 * Units. A PLANNED chain ('created' rows) carries book minutes — conserved
 * exactly — and OptiTime is derived for the technician. A LIVE chain
 * ('converted' rows: 1-minute shadows grown by durable accumulation) carries
 * its planned OptiTime in `opti_time`, which is conserved; shadows are never
 * rewritten. A chain on the BACKLOG (no technician) is re-dated over the
 * shop's open days and keeps its off-schedule shape (no finish, no OptiTime).
 *
 * History is frozen: started, running, paused, finished and tombstoned rows
 * are never moved, and a tail followed by started work is never extended
 * around it. Every write is one transaction; an unchanged plan is not written.
 */

import {
  buildChainReplan,
  chainReplanIsNoop,
  isUnresolvedChainRow,
  lineageFromHead,
  lostTimeShareMove,
  tailIsBoundedByHistory,
  unresolvedLineageTail,
  type ReplanChainRow,
} from './rolloverChainReplan.ts';
import {
  nextCalendarDate,
  planRolloverSegments,
  workCapacityFrom,
  type ResolveWorkDay,
} from './rolloverSegmentPlanner.ts';
import { buildTechnicianWorkDayResolver, loadTechnicianWorkDayResolver } from './rolloverWorkDay.ts';
import { calculateWallMinutes, wallToBookMinutes } from './canonicalTimeMath.ts';
import { getMinutesInShopTZ, makeUTCFromShopMinutes } from './timezoneMath.ts';
import { validTimeZoneOrNull } from './shopTime.ts';
import { workedMinutes, type TimerSession } from './finishIntentDerivation.ts';

// deno-lint-ignore no-explicit-any
type Db = any;

export const CHAIN_ROW_COLUMNS =
  'id, shop_id, status, deleted_at, actually_started, timer_status, rollover_type, predicted_minutes, opti_time, ' +
  'scheduled_date, start_time, opti_finish, assigned_tech_id, sequence_index, rollover_of_ticket_id, parent_ticket_id, ' +
  'deletion_reason';

export type ChainRow = ReplanChainRow & {
  shop_id: string; parent_ticket_id: string | null; deletion_reason?: string | null;
  /**
   * oct20d auditfix4 (PP): the row's STORED predicted_minutes. Inside the planner (withSnapshot) `predicted_minutes` /
   * `opti_time` are the chain AUTHORITY's values (run_long_chain_authority_shares — what a server path last wrote, or
   * the creation value); this keeps the stored column for the compare-and-set the RPC checks against.
   */
  stored_predicted_minutes?: number | null;
};

export interface ChainSnapshot {
  row: ChainRow;
  rows: ChainRow[];
  timezone: string;
}

export type ChainReplanOutcome =
  | { status: 'applied'; written: number; lanes: Array<{ date: string; techId: string | null }> }
  | { status: 'noop'; ledger?: string }
  | { status: 'refused'; reason: string }
  | { status: 'conflict' };

interface Point { date: string; min: number }
const later = (a: Point, b: Point): Point => (b.date > a.date || (b.date === a.date && b.min > a.min) ? b : a);

function dayLabel(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(iso));
}
function pointOf(iso: string, tz: string): Point {
  return { date: dayLabel(iso, tz), min: getMinutesInShopTZ(iso, tz) };
}

const isTombstoned = (r: ReplanChainRow) => !!r.deleted_at || r.status === 'Deleted' || r.status === 'Cancelled';

/**
 * CANONICAL CHAIN IDENTITY. A row's future schedule belongs to the chain
 * planner when it is unresolved AND it is part of a chain's lineage: it
 * continues another row, or a live row continues it. That — not its minutes,
 * not a 1-minute shadow — is what makes a lane cascade, an AutoBump or a date
 * change hand it to this authority instead of re-timing it.
 */
export function isChainGoverned(row: ReplanChainRow, rows: ReplanChainRow[]): boolean {
  if (!isUnresolvedChainRow(row)) return false;
  if (row.rollover_of_ticket_id) return true;
  return rows.some((r) => r.rollover_of_ticket_id === row.id && !isTombstoned(r));
}

/** The row, its whole chain (root + every row rooted at it) and the shop's zone. */
export async function loadChainSnapshot(db: Db, ticketId: string): Promise<ChainSnapshot | null> {
  const { data: row, error } = await db.from('tickets').select(CHAIN_ROW_COLUMNS).eq('id', ticketId).maybeSingle();
  if (error || !row) return null;
  const r = row as ChainRow;
  const root = r.parent_ticket_id ?? r.id;
  const [{ data: rows, error: rowsError }, { data: shop }] = await Promise.all([
    db.from('tickets').select(CHAIN_ROW_COLUMNS).or(`id.eq.${root},parent_ticket_id.eq.${root}`),
    db.from('shop_profile').select('time_zone').eq('shop_id', r.shop_id).maybeSingle(),
  ]);
  if (rowsError || !rows) return null;
  return { row: r, rows: rows as ChainRow[], timezone: shop?.time_zone || 'America/Los_Angeles' };
}

/**
 * oct20c (PR #13 audit, finding 1): the same snapshot, FAIL-CLOSED — a read error THROWS (never "not a chain"), and a
 * shop whose zone is missing or unknown throws (never a default zone). Null only when the row itself does not exist.
 */
export async function loadChainSnapshotStrict(db: Db, ticketId: string): Promise<ChainSnapshot | null> {
  const { data: row, error } = await db.from('tickets').select(CHAIN_ROW_COLUMNS).eq('id', ticketId).maybeSingle();
  if (error) throw new Error(`chain row unreadable: ${error.message}`);
  if (!row) return null;
  const r = row as ChainRow;
  const root = r.parent_ticket_id ?? r.id;
  const [{ data: rows, error: rowsError }, { data: shop, error: shopError }] = await Promise.all([
    db.from('tickets').select(CHAIN_ROW_COLUMNS).or(`id.eq.${root},parent_ticket_id.eq.${root}`),
    db.from('shop_profile').select('time_zone').eq('shop_id', r.shop_id).maybeSingle(),
  ]);
  if (rowsError || !rows) throw new Error(`chain rows unreadable: ${rowsError?.message ?? 'no rows'}`);
  if (shopError) throw new Error(`shop profile unreadable: ${shopError.message}`);
  const timezone = validTimeZoneOrNull(shop?.time_zone);
  if (!timezone) throw new Error('shop time zone missing or unknown');
  return { row: r, rows: rows as ChainRow[], timezone };
}

/**
 * oct20c (PR #13 audit, finding 2): a chain growth that was CLAIMED into the durable pending-growth ledger
 * (run_long_chain_growth). Its re-plan is persisted through apply_run_long_chain_growth, which writes the chain rows
 * (the same replan_rollover_chain_from_source) AND marks the ledger row applied in one transaction — an already
 * applied (or voided) row writes nothing, so a retry can never add the minutes twice.
 */
export interface GrowthLedgerRef { ledgerId: string; holder: string | null }

/**
 * Persist a CLAIMED growth's re-plan through the ledger-guarded RPC (which calls replan_rollover_chain_from_source
 * itself, in the same transaction as the ledger mark). An ordinary re-plan calls the chain RPC directly.
 */
async function persistGrowth(db: Db, growth: GrowthLedgerRef, args: {
  p_source: unknown; p_updates: unknown; p_inserts: unknown; p_retire: unknown;
}): Promise<{ error: { code?: string; message: string } | null; alreadySettled?: string }> {
  const { data, error } = await db.rpc('apply_run_long_chain_growth', { p_ledger: growth.ledgerId, p_holder: growth.holder, ...args });
  if (error) return { error };
  const outcome = String((data as { outcome?: string } | null)?.outcome ?? '');
  if (outcome === 'APPLIED') return { error: null };
  if (outcome.startsWith('ALREADY_')) return { error: null, alreadySettled: outcome };
  return { error: { message: `growth ledger ${growth.ledgerId}: ${outcome || 'no answer'}` } };
}

/** A technician's efficiency, buffer and dated workdays — or the shop's open days for a backlog chain. */
async function planningCalendar(db: Db, shopId: string, techId: string | null, fromDateStr: string): Promise<{
  eff: number; buf: number; resolveDay: ResolveWorkDay;
} | { redate: true; holidays: Set<string> } | null> {
  if (techId) {
    const { data: tech } = await db.from('technicians')
      .select('efficiency_percentage, buffer_percentage').eq('id', techId).maybeSingle();
    if (!tech) return null;
    // CSI-S2 / DMC-G2: the day comes from the one dated workday resolver; the
    // flat-shift fallback lives only inside it.
    const resolveDay = await loadTechnicianWorkDayResolver(db, { shopId, techId, fromDateStr });
    return { eff: tech.efficiency_percentage || 100, buf: tech.buffer_percentage ?? 0, resolveDay };
  }
  // Backlog: nobody's hours yet — the shop's own open days and holidays.
  const { data: shop } = await db.from('shop_profile').select('hours, holidays').eq('shop_id', shopId).maybeSingle();
  if (!shop) return null;
  const hasHours = !!shop.hours && typeof shop.hours === 'object' && Object.keys(shop.hours).length > 0;
  if (!hasHours) {
    // No shop hours to plan capacity against: the chain keeps its day-split
    // and is re-dated onto the shop's open days (weekdays that are not
    // holidays — the parking-lot rollover's own rule), in lineage order.
    return {
      redate: true,
      holidays: new Set(((shop.holidays ?? []) as Array<{ date?: string | null }>).map((h) => h?.date).filter(Boolean) as string[]),
    };
  }
  return {
    eff: 100,
    buf: 0,
    resolveDay: buildTechnicianWorkDayResolver({ hours: shop.hours, time_off: [] }, { holidays: shop.holidays ?? [] }),
  };
}

/**
 * RE-PLAN AN UNRESOLVED TAIL AND PERSIST IT ATOMICALLY — the single body every
 * public operation below ends in.
 */
async function replanTail(db: Db, snap: ChainSnapshot, a: {
  /** The history row the tail follows; null when the tail is the head of the chain. */
  predecessor: ChainRow | null;
  tail: ChainRow[];
  /** Row to clone when the tail has no rows of its own (a last segment growing). */
  templateId?: string;
  /** Planned chains: book minutes added to the tail's pool. */
  extraBook: number;
  /** Live chains: OptiTime added to the tail's pool. */
  extraWork: number;
  start: Point;
  /**
   * Only a boundary that moved EARLIER on purpose (a shrink, an operator's
   * earlier date) may pull the tail earlier. Otherwise the tail starts at the
   * later of the boundary and where it already starts — so a call that
   * changes nothing writes nothing.
   */
  mayPullEarlier: boolean;
  /** The source's new planned book minutes, when it changes (compare-and-set). */
  source?: { id: string; predicted: number; expected: number | null } | null;
  /**
   * oct20d auditfix2 (CS): send the source share even when it is unchanged, so the database re-aligns a started
   * row's frozen approved expectation with the share this re-plan gives it (replan_rollover_chain_from_source
   * confirms an unchanged share under the row lock without re-writing it). A job change on a chain row is booked
   * on the row that carries the line; the re-plan may put that work on a later day instead.
   */
  syncSourceShare?: boolean;
  /**
   * oct20d auditfix3 (JH): this plan was derived from the chain's JOB LEDGER (applyChainWorkChange). The database then
   * lets the chain's other started rows return approved work the plan put on another row (a job added on yesterday's
   * stopped head, planned on today's continuation). Never sent by a growth / pushback re-plan, which moves no ledger work.
   */
  releaseHeldWork?: boolean;
  /**
   * A HARD start (a Waiter's promised time): the first re-planned day must
   * begin exactly here, or nothing is written. The planner never moves a
   * promise to a later minute on its own.
   */
  requireStart?: Point;
  label: string;
  /** oct20c: a claimed growth — persisted through the ledger-guarded RPC (exactly once). */
  growth?: GrowthLedgerRef;
}): Promise<ChainReplanOutcome> {
  const { tail, predecessor } = a;
  const shopId = snap.row.shop_id;
  const tz = snap.timezone;
  const templateRow = tail.length ? tail[tail.length - 1] : snap.rows.find((r) => r.id === a.templateId) ?? null;
  const techs = [...new Set((tail.length ? tail : templateRow ? [templateRow] : []).map((r) => r.assigned_tech_id ?? null))];
  if (techs.length !== 1) {
    // A tail split across technicians is a reassignment's decision, not this one's.
    return { status: 'refused', reason: 'tail is not on one technician' };
  }
  const techId = techs[0];
  const live = tail.some((r) => r.rollover_type === 'converted') || templateRow?.rollover_type === 'converted'
    || (predecessor?.rollover_type === 'converted' && tail.length === 0);
  const offSchedule = techId === null;

  let start = a.start;
  if (!a.mayPullEarlier && tail[0]?.scheduled_date && tail[0].start_time && !offSchedule) {
    const cur = pointOf(tail[0].start_time, tz);
    start = later(start, { date: tail[0].scheduled_date.slice(0, 10), min: cur.date === tail[0].scheduled_date.slice(0, 10) ? cur.min : 0 });
  }

  const calendar = await planningCalendar(db, shopId, techId, start.date);
  if (!calendar) return { status: 'refused', reason: 'no calendar for the chain' };
  if ('redate' in calendar) {
    if (a.requireStart) return { status: 'refused', reason: 'a chain on the backlog has no technician to hold a promised time' };
    return redateBacklogChain(db, snap, { predecessor, tail, start, extraBook: a.extraBook, holidays: calendar.holidays, label: a.label, growth: a.growth });
  }
  const cal = calendar;

  let poolWork: number;
  let tailBook: number | null;
  if (live) {
    if (offSchedule || tail.some((r) => !(r.opti_time && r.opti_time > 0))) {
      return { status: 'refused', reason: 'a live continuation carries no planned OptiTime' };
    }
    tailBook = null;
    // A negative extra (less work) shrinks the planned OptiTime, never below zero.
    poolWork = Math.max(0, tail.reduce((n, r) => n + (r.opti_time ?? 0), 0) + a.extraWork);
  } else {
    tailBook = tail.reduce((n, r) => n + (r.predicted_minutes ?? 0), 0) + a.extraBook;
    if (tailBook < 0) tailBook = 0;
    poolWork = calculateWallMinutes(tailBook, cal.eff, cal.buf);
  }

  // Started work already follows the tail's last row (or the row an empty
  // tail would grow from): the tail may be reused or shortened, never extended.
  const bounded = tailIsBoundedByHistory(a.templateId ?? predecessor?.id ?? '', tail, snap.rows);
  const built = buildChainReplan({
    predecessorId: predecessor?.id ?? null,
    seqBase: predecessor?.sequence_index ?? (tail[0]?.sequence_index ? tail[0].sequence_index - 1 : 0),
    tail,
    templateId: a.templateId,
    techId,
    poolWorkMinutes: poolWork,
    tailBookMinutes: tailBook,
    efficiencyPct: cal.eff,
    bufferPct: cal.buf,
    startDateStr: start.date,
    startMin: start.min,
    resolveDay: cal.resolveDay,
    timezone: tz,
    offSchedule,
    idStamp: Date.now(),
  });
  if (built.ok === false) {
    const refused = built as Extract<typeof built, { ok: false }>;
    console.warn(`[ChainAuthority] ${a.label}: ${refused.reason} (${refused.unplacedMinutes} of ${poolWork} min) — chain left as it was`);
    return { status: 'refused', reason: refused.reason };
  }
  const plan = built as Extract<typeof built, { ok: true }>;
  if (a.requireStart) {
    const first = plan.segments[0];
    if (!first || first.dateStr !== a.requireStart.date || first.startMin !== a.requireStart.min) {
      return { status: 'refused', reason: 'the promised time is not a valid work opportunity for this chain' };
    }
  }
  if (bounded && plan.inserts.length > 0) {
    console.warn(`[ChainAuthority] ${a.label}: the tail is followed by started work and would need more days — chain left as it was`);
    return { status: 'refused', reason: 'tail bounded by history' };
  }

  const sourceChanges = !!a.source && a.source.predicted !== a.source.expected;
  const tailUnchanged = chainReplanIsNoop(tail, plan);
  // oct20d auditfix2 (CS): nothing to re-plan, but the started source's frozen expectation may still differ from the
  // share this plan confirms (a job edit booked on it, or a split that set its share directly): confirm the share
  // through the same RPC — it writes no row, only the expectation revision when one is due.
  const confirmOnly = !sourceChanges && tailUnchanged && !!a.syncSourceShare && !!a.source;
  if (!confirmOnly) {
    if (!sourceChanges && tailUnchanged) return { status: 'noop' };
  }
  if (tailUnchanged) {
    // Only the source's share moves: rows already on plan are not rewritten.
    plan.updates = [];
    plan.inserts = [];
    plan.retire = [];
    plan.segments = [];
  }

  const shareSource = sourceChanges || (a.syncSourceShare && a.source)
    ? { id: a.source!.id, predicted_minutes: a.source!.predicted, expected_predicted_minutes: a.source!.expected }
    : null;
  const p_source = a.releaseHeldWork ? { ...(shareSource ?? {}), release_held_work: true } : shareSource;
  const args = { p_source, p_updates: plan.updates, p_inserts: plan.inserts, p_retire: plan.retire };
  const { error, alreadySettled } = a.growth
    ? await persistGrowth(db, a.growth, args)
    : await db.rpc('replan_rollover_chain_from_source', args);
  if (alreadySettled) return { status: 'noop', ledger: alreadySettled };
  if (error) {
    if (error.code === '55000' || error.code === '40001') return { status: 'conflict' }; // R5: 55000 (40001 hangs in PostgREST)
    console.warn(`[ChainAuthority] ${a.label}: re-plan refused, chain left as it was: ${error.message}`);
    return { status: 'refused', reason: error.message };
  }
  if (confirmOnly) return { status: 'noop' };   // the schedule did not change; only the share was confirmed
  console.log(tailUnchanged
    ? `[ChainAuthority] ${a.label}: source share ${a.source?.predicted}min; tail already on plan`
    : `[ChainAuthority] ${a.label}: ${poolWork}min over ${plan.segments.length} day(s); ` +
      `reuse ${plan.updates.length}, insert ${plan.inserts.length}, retire ${plan.retire.length}`);

  const lanes = new Map<string, { date: string; techId: string | null }>();
  for (const l of [
    ...tail.map((r) => ({ date: String(r.scheduled_date ?? '').slice(0, 10), techId: r.assigned_tech_id })),
    ...plan.segments.map((s) => ({ date: s.dateStr, techId })),
  ]) if (l.date) lanes.set(`${l.date}:${l.techId ?? ''}`, l);
  return {
    status: 'applied',
    written: plan.updates.length + plan.inserts.length + plan.retire.length + (sourceChanges ? 1 : 0),
    lanes: [...lanes.values()],
  };
}

/**
 * A BACKLOG chain with no shop hours: no technician, no capacity to plan
 * against. Its rows keep their book shares (the last one takes any extra) and
 * move, in lineage order, one per open day from `start` — the same open days
 * the parking-lot rollover uses (not a weekend, not a holiday). Off-schedule
 * shape kept: no finish, no OptiTime, no technician. Persisted through the
 * same RPC, all or nothing.
 */
async function redateBacklogChain(db: Db, snap: ChainSnapshot, a: {
  predecessor: ChainRow | null; tail: ChainRow[]; start: Point; extraBook: number;
  holidays: Set<string>; label: string; growth?: GrowthLedgerRef;
}): Promise<ChainReplanOutcome> {
  if (a.tail.length === 0) return { status: 'noop' };
  const tz = snap.timezone;
  const isOpen = (d: string) => {
    const dow = new Date(`${d}T12:00:00Z`).getUTCDay();
    return dow !== 0 && dow !== 6 && !a.holidays.has(d);
  };
  const seqBase = a.predecessor?.sequence_index ?? ((a.tail[0].sequence_index ?? 1) - 1);
  const updates: Array<Record<string, unknown>> = [];
  let d = a.start.date;
  for (let k = 0; k < a.tail.length; k++) {
    for (let i = 0; i < 60 && !isOpen(d); i++) d = nextCalendarDate(d);
    if (!isOpen(d)) return { status: 'refused', reason: 'NO_WORKDAY_IN_HORIZON' };
    const r = a.tail[k];
    const minute = r.start_time ? getMinutesInShopTZ(r.start_time, tz) : 0;
    const book = (r.predicted_minutes ?? 0) + (k === a.tail.length - 1 ? a.extraBook : 0);
    updates.push({
      id: r.id,
      assigned_tech_id: null,
      scheduled_date: d,
      start_time: makeUTCFromShopMinutes(d, minute, tz).toISOString(),
      opti_finish: null,
      opti_time: null,
      predicted_minutes: Math.max(1, book),
      sequence_index: seqBase + k + 1,
      rollover_of_ticket_id: k === 0 ? (a.predecessor?.id ?? null) : a.tail[k - 1].id,
    });
    d = nextCalendarDate(d);
  }
  const unchanged = updates.every((u, k) => {
    const r = a.tail[k];
    return (r.scheduled_date ?? '').slice(0, 10) === u.scheduled_date && r.predicted_minutes === u.predicted_minutes
      && (r.sequence_index ?? null) === u.sequence_index && (r.rollover_of_ticket_id ?? null) === u.rollover_of_ticket_id
      && !r.opti_finish && r.opti_time == null && !r.assigned_tech_id;
  });
  if (unchanged) return { status: 'noop' };
  const args = { p_source: null, p_updates: updates, p_inserts: [], p_retire: [] };
  const { error, alreadySettled } = a.growth
    ? await persistGrowth(db, a.growth, args)
    : await db.rpc('replan_rollover_chain_from_source', args);
  if (alreadySettled) return { status: 'noop', ledger: alreadySettled };
  if (error) {
    if (error.code === '55000' || error.code === '40001') return { status: 'conflict' }; // R5: 55000 (40001 hangs in PostgREST)
    console.warn(`[ChainAuthority] ${a.label}: backlog re-date refused, chain left as it was: ${error.message}`);
    return { status: 'refused', reason: error.message };
  }
  console.log(`[ChainAuthority] ${a.label}: backlog chain re-dated from ${a.start.date} (${updates.length} row(s))`);
  return {
    status: 'applied',
    written: updates.length,
    lanes: updates.map((u) => ({ date: String(u.scheduled_date), techId: null })),
  };
}

/**
 * oct20d auditfix4 (PP): the snapshot as the chain authority knows it. Each row's `predicted_minutes` / `opti_time`
 * becomes its authority share (run_long_chain_authority_shares: the value a server path last wrote, or the value the
 * row was created with — the same record the database's freeze and share rules read, __run_long_chain_share).
 * `stored_predicted_minutes` keeps the column for compare-and-set.
 * Null when the record is unreadable OR a row has no record (the caller refuses / throws; it never plans from raw
 * values instead). oct20d auditfix5 (audit5 LG): every ticket has a record — 20270211500000 backfilled the terminal and
 * deleted rows the first backfill skipped, and every INSERT records one — so a missing record is never "keep the raw,
 * possibly client-written column"; and a record's NULL opti_time stays NULL (a client can PATCH the column).
 */
async function withAuthorityShares(db: Db, snap: ChainSnapshot): Promise<ChainSnapshot | null> {
  const ids = [...new Set([snap.row.id, ...snap.rows.map((r) => r.id)])];
  const { data, error } = await db.from('run_long_chain_authority_shares')
    .select('ticket_id, share_minutes, opti_time').in('ticket_id', ids);
  if (error || !Array.isArray(data)) return null;
  const byId = new Map((data as Array<{ ticket_id: string; share_minutes: number | null; opti_time: number | null }>)
    .map((s) => [s.ticket_id, s]));
  const missing = ids.filter((id) => !byId.has(id));
  if (missing.length > 0) {
    console.warn(`[ChainAuthority] no authority record for ${missing.join(', ')}: refused, never planned from raw values`);
    return null;
  }
  const overlay = (r: ChainRow): ChainRow => {
    const s = byId.get(r.id)!;
    return {
      ...r,
      stored_predicted_minutes: r.predicted_minutes,
      predicted_minutes: s.share_minutes,
      opti_time: s.opti_time,
    };
  };
  const rows = snap.rows.map(overlay);
  return { ...snap, row: rows.find((r) => r.id === snap.row.id) ?? overlay(snap.row), rows };
}

/** The stored predicted_minutes (compare-and-set expectation), whatever the planner's view of the share is. */
const storedPredicted = (r: ChainRow): number | null =>
  r.stored_predicted_minutes !== undefined ? r.stored_predicted_minutes : r.predicted_minutes;

/** Run an operation against a fresh snapshot; re-read and retry on a compare-and-set conflict. */
async function withSnapshot(
  db: Db, ticketId: string,
  run: (snap: ChainSnapshot) => Promise<ChainReplanOutcome | null>,
  opts: { strict?: boolean } = {},
): Promise<ChainReplanOutcome | null> {
  for (let attempt = 0; attempt < 3; attempt++) {
    // oct20c: a claimed growth reads fail-closed (a read error throws; no default zone)
    const snap = opts.strict ? await loadChainSnapshotStrict(db, ticketId) : await loadChainSnapshot(db, ticketId);
    if (!snap) return null;
    // oct20d auditfix4 (PP): the planner plans from the chain AUTHORITY's shares, never from a value a signed-in
    // client could have written (bf Q4: a client write of a chain row's predicted_minutes is scheduling only).
    const shared = await withAuthorityShares(db, snap);
    if (!shared) {
      if (opts.strict) throw new Error('chain authority shares unreadable');
      return { status: 'refused', reason: 'chain authority shares unreadable' };
    }
    const out = await run(shared);
    if (!out || out.status !== 'conflict') return out;
    await new Promise((r) => setTimeout(r, [50, 150, 450][attempt]));
  }
  return { status: 'conflict' };
}

/** The unresolved future after `sourceId`, by lineage. */
function tailAfter(snap: ChainSnapshot, sourceId: string): ChainRow[] {
  return unresolvedLineageTail(sourceId, snap.rows) as ChainRow[];
}

/** Where a history row ends: its stored finish, or the day after it when it has none (backlog). */
function endOf(row: ChainRow, tz: string): Point | null {
  if (row.opti_finish) return pointOf(row.opti_finish, tz);
  if (row.scheduled_date) return { date: nextCalendarDate(row.scheduled_date.slice(0, 10)), min: 0 };
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// PUBLIC OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PUSHBACK: the source now finishes later. It is history — never moved — and
 * its finish is the earliest valid start for the rest of the work. Never
 * pulls the tail earlier.
 */
export async function replanAfterPushback(db: Db, sourceId: string): Promise<ChainReplanOutcome | null> {
  return withSnapshot(db, sourceId, async (snap) => {
    const tail = tailAfter(snap, sourceId);
    if (tail.length === 0) return null;
    const end = endOf(snap.row, snap.timezone);
    if (!end) return null;
    return replanTail(db, snap, {
      predecessor: snap.row, tail, extraBook: 0, extraWork: 0,
      start: later(end, { date: snap.row.scheduled_date!.slice(0, 10), min: 0 }),
      mayPullEarlier: false, label: `pushback ${sourceId}`,
    });
  });
}

/**
 * MORE FUTURE WORK: the source could not do all of its planned share today (a pause, a break, a parts wait, or its
 * day ended). NOV 3 (founder Decisions 2 + 3): lost time MOVES work, it never CREATES work. The caller's
 * `extraWallMinutes` (a pause / run-long watermark delta) is only the TRIGGER — it is never converted into book
 * minutes. The source keeps what it did plus what its own day can still hold (lostTimeShareMove); exactly the
 * rest of its share joins the chain's unresolved future, and the source's share is lowered by the same amount in
 * the same transaction (replan_rollover_chain_from_source rule 4 then moves its frozen expectation with an audited
 * `chain_share_moved` revision; revision 0 is never rewritten). Σ shares is conserved: pauses, breaks, overnight
 * time and run-long add 0 to the repair. A run-long source moves nothing (its overrun is counted once, in its own
 * finish). A live (converted) chain carries its work as planned OptiTime with no per-row share to lower, so no
 * growth is added to it here (its pause shadow stays the conversion maintainer's).
 */
/** Mid-day share moves happen in steps of at least this many book minutes (the day's end always moves exactly). */
export const LOST_TIME_MID_DAY_STEP = 30;

export async function addFutureWorkAfter(
  db: Db, sourceId: string, extraWallMinutes: number, opts: { growth?: GrowthLedgerRef; nowMs?: number } = {},
): Promise<ChainReplanOutcome | null> {
  if (!(extraWallMinutes > 0)) return null;
  const nowMs = opts.nowMs ?? Date.now();
  return withSnapshot(db, sourceId, async (snap) => {
    const src = snap.row;
    const tail = tailAfter(snap, sourceId);
    const templateId = tail.length ? undefined : sourceId;
    const templateRow = tail.length ? tail[tail.length - 1] : src;
    const live = tail.some((r) => r.rollover_type === 'converted') || templateRow.rollover_type === 'converted'
      || src.rollover_type === 'converted';
    if (live) return { status: 'noop' };
    // only started, live work has lost time to move
    if (!src.actually_started || isTombstoned(src) || (src.status !== 'Active' && src.status !== 'Paused')) {
      return { status: 'noop' };
    }
    const srcDate = (src.scheduled_date ?? '').slice(0, 10);
    if (!srcDate) return { status: 'refused', reason: 'source has no day' };
    const techId = src.assigned_tech_id ?? tail[0]?.assigned_tech_id ?? null;
    const [{ data: sessions, error: sessErr }, { data: tech, error: techError }, { data: frozen, error: frozenErr }] = await Promise.all([
      db.from('ticket_timer_sessions').select('duration_min, started_at, stopped_at').eq('ticket_id', src.id),
      techId
        ? db.from('technicians').select('efficiency_percentage, buffer_percentage').eq('id', techId).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
      // the factors this row's share was frozen at (its book ↔ wall rate): stable across share moves, unlike opti_time,
      // which a share move does not rewrite
      db.from('ticket_run_long_baseline_revisions').select('efficiency_pct, buffer_pct')
        .eq('ticket_id', src.id).order('revision_no', { ascending: false }).limit(1).maybeSingle(),
    ]);
    // never "no work": an unread session list would move the whole share
    if (sessErr || !Array.isArray(sessions)) {
      if (opts.growth) throw new Error(`timer sessions unreadable: ${sessErr?.message ?? 'no rows'}`);
      return { status: 'refused', reason: 'timer sessions unreadable' };
    }
    // oct20c: a claimed growth never converts its minutes with a defaulted efficiency
    if (opts.growth && (techError || (techId && !tech))) throw new Error(`technician unreadable: ${techError?.message ?? 'missing'}`);
    if (opts.growth && frozenErr) throw new Error(`frozen expectation unreadable: ${frozenErr.message}`);
    const eff = frozen?.efficiency_pct || tech?.efficiency_percentage || 100;
    const buf = frozen?.buffer_pct ?? tech?.buffer_percentage ?? 0;
    const today = dayLabel(new Date(nowMs).toISOString(), snap.timezone);
    // what the source's OWN day can still hold (0 once that day is over: overnight time never moves more work)
    let capacityLeftWall = 0;
    if (srcDate >= today && techId) {
      const calendar = await planningCalendar(db, src.shop_id, techId, srcDate);
      if (calendar && !('redate' in calendar)) {
        const day = calendar.resolveDay(srcDate);
        const fromMin = srcDate === today ? getMinutesInShopTZ(new Date(nowMs).toISOString(), snap.timezone) : 0;
        capacityLeftWall = day ? workCapacityFrom(day, fromMin) : 0;
      }
    }
    const move = lostTimeShareMove({
      share: src.predicted_minutes, optiTime: null,
      workedMinutes: workedMinutes(sessions as TimerSession[], nowMs), capacityLeftWall,
      efficiencyPct: eff, bufferPct: buf, minMidDayStep: LOST_TIME_MID_DAY_STEP,
    });
    if (move.moved <= 0) return { status: 'noop' };
    return replanTail(db, snap, {
      predecessor: src, tail, templateId, extraBook: move.moved, extraWork: 0,
      start: { date: nextCalendarDate(srcDate), min: 0 },
      mayPullEarlier: false,
      source: { id: src.id, predicted: move.keep, expected: storedPredicted(src) ?? null },
      label: `lost time on ${sourceId}: ${move.moved} of ${src.predicted_minutes} booked minutes move to the chain's future`,
      growth: opts.growth,
    });
  }, { strict: !!opts.growth });
}

/**
 * MOVE A CHAIN'S FRONTIER: an unresolved chain row must start no earlier than
 * `floor` (a lane cascade or AutoBump pushed it later; the parking-lot cron
 * rolled it; an operator picked a new date). The row and every unresolved row
 * after it are re-planned as ONE pool from the first valid opportunity at or
 * after the floor — never before the row it follows has finished. The row is
 * never detached from its chain; history is never moved.
 */
export async function replanFromRow(
  db: Db, rowId: string, floor: Point,
  opts: { mayPullEarlier?: boolean; label?: string; requireStartAtFloor?: boolean } = {},
): Promise<ChainReplanOutcome | null> {
  return withSnapshot(db, rowId, async (snap) => {
    if (!isUnresolvedChainRow(snap.row)) return { status: 'refused', reason: 'row is history' };
    const predecessor = snap.row.rollover_of_ticket_id
      ? snap.rows.find((r) => r.id === snap.row.rollover_of_ticket_id && !isTombstoned(r)) ?? null
      : null;
    if (snap.row.rollover_of_ticket_id && !predecessor) return { status: 'refused', reason: 'broken lineage' };
    const tail = [snap.row, ...tailAfter(snap, snap.row.id)];
    let start = floor;
    if (predecessor) {
      const end = endOf(predecessor, snap.timezone);
      if (end) start = later(start, end);
    }
    return replanTail(db, snap, {
      predecessor, tail, extraBook: 0, extraWork: 0, start,
      mayPullEarlier: opts.mayPullEarlier ?? false,
      requireStart: opts.requireStartAtFloor ? floor : undefined,
      label: opts.label ?? `frontier ${rowId}`,
    });
  });
}

/**
 * THE CHAIN'S JOB LEDGER — the one authoritative total of book minutes.
 *
 * A chain's work is its job lines, not any row's `predicted_minutes`:
 *   - a booked (smart-assign) chain puts every job line on the ROOT; its
 *     continuations carry none (only their book share);
 *   - a live carry's continuations carry none (the copy never lands);
 *   - a manual split's EOD continuations carry COPIES of the part's lines
 *     (same job_id, full minutes) — the same work, not more of it;
 *   - a job added while viewing a continuation lands on that continuation.
 * So the ledger is the chain's lines deduplicated by job_id (the head's own
 * line wins), each worth `minutes_override ?? jobs.standard_minutes`. Lines of
 * rows the re-plan superseded still count — a retired day never takes a job
 * with it — but a cancelled or deleted ticket's lines do not.
 */
const SUPERSEDED_REASON = 'Superseded by rollover chain re-plan';

async function chainJobLedgerBook(db: Db, headId: string, rows: ChainRow[]): Promise<number | null> {
  const ids = rows
    .filter((r) => !isTombstoned(r) || r.deletion_reason === SUPERSEDED_REASON)
    .map((r) => r.id);
  if (ids.length === 0) return null;
  const { data, error } = await db.from('ticket_jobs')
    .select('ticket_id, job_id, minutes_override, deleted_at, jobs:job_id(standard_minutes)')
    .in('ticket_id', ids);
  if (error) return null;
  const byJob = new Map<string, number>();
  const lines = ((data ?? []) as Array<{ ticket_id: string; job_id: string; minutes_override: number | null;
    deleted_at: string | null; jobs: { standard_minutes: number | null } | null }>)
    .filter((l) => !l.deleted_at)
    .sort((x, y) => (x.ticket_id === headId ? -1 : y.ticket_id === headId ? 1 : 0));
  for (const l of lines) {
    if (byJob.has(l.job_id)) continue;
    byJob.set(l.job_id, l.minutes_override ?? l.jobs?.standard_minutes ?? 0);
  }
  return [...byJob.values()].reduce((n, v) => n + v, 0);
}

function shopToday(tz: string): string {
  return dayLabel(new Date().toISOString(), tz);
}

/**
 * WORK CHANGE — the ONE entry for every job add / remove / edit / DVI /
 * canned-job / intake write on a chain row. The caller changes JOB LINES; it
 * never writes a chain row's `predicted_minutes`. This then:
 *
 *   1. computes the chain's authoritative total once (the job ledger);
 *   2. takes what history has already consumed (the planned shares of rows
 *      that are started, finished or past);
 *   3. derives the unresolved future work;
 *   4. re-plans the unresolved tail ONCE — adding a day after the last row
 *      when the future outgrows it, retiring days when it shrinks.
 *
 * PLANNED chain (book shares): total − history = the future's book, exact. A
 * row being worked TODAY may keep up to its dated day's capacity (it can still
 * do it today); the rest is the tail's. LIVE chain (converted placeholders):
 * the head carries the chain's total as its planned minutes; a change of the
 * total (ΔT) changes the future's planned OptiTime by the same work, and the
 * shadows are never touched. A ticket that is not a multi-row lineage returns
 * null — the caller's ordinary single-ticket path applies.
 */
export async function applyChainWorkChange(db: Db, anyRowId: string): Promise<ChainReplanOutcome | null> {
  return withSnapshot(db, anyRowId, async (snap) => {
    const tz = snap.timezone;
    const headId = snap.row.parent_ticket_id ?? snap.row.id;
    const lineage = lineageFromHead(headId, snap.rows) as ChainRow[];
    if (lineage.length < 2 || !lineage.some((r) => r.id === snap.row.id)) return null;
    const T = await chainJobLedgerBook(db, headId, snap.rows);
    if (T === null) return { status: 'refused', reason: 'job ledger unreadable' };

    // History = the lineage up to the first unresolved row; the open tail is
    // the unresolved run after it (stopping at any later started row).
    const firstOpen = lineage.findIndex((r) => isUnresolvedChainRow(r));
    const history = firstOpen === -1 ? lineage : lineage.slice(0, firstOpen);
    const openTail: ChainRow[] = [];
    if (firstOpen !== -1) {
      for (const r of lineage.slice(firstOpen)) {
        if (!isUnresolvedChainRow(r)) break;
        openTail.push(r);
      }
    }
    const last = history.length ? history[history.length - 1] : null;
    const live = lineage.some((r) => r.rollover_type === 'converted');
    const label = `work change ${anyRowId}`;

    if (live) {
      const head = lineage[0];
      const delta = T - (head.predicted_minutes ?? 0);
      if (delta === 0) return { status: 'noop' };
      const pred = last ?? head;
      const techId = (openTail[0] ?? pred).assigned_tech_id;
      const { data: tech } = techId
        ? await db.from('technicians').select('efficiency_percentage, buffer_percentage').eq('id', techId).maybeSingle()
        : { data: null };
      const eff = tech?.efficiency_percentage || 100;
      const buf = tech?.buffer_percentage ?? 0;
      const deltaWork = delta > 0 ? calculateWallMinutes(delta, eff, buf) : -calculateWallMinutes(-delta, eff, buf);
      const predDate = (pred.scheduled_date ?? shopToday(tz)).slice(0, 10);
      return replanTail(db, snap, {
        predecessor: last, tail: openTail, templateId: openTail.length ? undefined : pred.id,
        extraBook: 0, extraWork: deltaWork,
        start: { date: nextCalendarDate(predDate), min: 0 },
        mayPullEarlier: delta < 0,
        source: { id: head.id, predicted: T, expected: storedPredicted(head) ?? null }, label,
      });
    }

    // PLANNED. A row being worked today may keep what fits its dated day.
    const today = shopToday(tz);
    const adjustable = last && last.status !== 'Completed' && !isTombstoned(last) && last.assigned_tech_id
      && last.start_time && (last.scheduled_date ?? '').slice(0, 10) >= today ? last : null;
    // oct20d auditfix4 (PP): history contributes its AUTHORITY shares (withSnapshot), never a raw client write
    const frozen = history.filter((r) => r !== adjustable).reduce((n, r) => n + (r.predicted_minutes ?? 0), 0);
    let future = Math.max(0, T - frozen);
    let source: { id: string; predicted: number; expected: number | null } | null = null;
    let start: Point;
    let predecessor: ChainRow | null = last;

    if (adjustable) {
      const date = adjustable.scheduled_date!.slice(0, 10);
      const calendar = await planningCalendar(db, adjustable.shop_id, adjustable.assigned_tech_id, date);
      if (!calendar || 'redate' in calendar) return { status: 'refused', reason: 'no calendar for the chain' };
      const cal = calendar;
      const day = cal.resolveDay(date);
      const startMin = getMinutesInShopTZ(adjustable.start_time!, tz);
      const capBook = day ? wallToBookMinutes(workCapacityFrom(day, startMin), cal.eff, cal.buf)
        : (adjustable.predicted_minutes ?? 0);
      const share = Math.max(1, Math.min(future, capBook));
      future -= share;
      source = { id: adjustable.id, predicted: share, expected: storedPredicted(adjustable) ?? null };
      if (future > 0 || !day) {
        start = { date: nextCalendarDate(date), min: 0 };
      } else {
        const own = planRolloverSegments({ totalWorkMinutes: calculateWallMinutes(share, cal.eff, cal.buf),
          startDateStr: date, startMin, resolveDay: cal.resolveDay });
        start = { date, min: own.segments.length ? own.segments[own.segments.length - 1].finishMin : startMin };
      }
    } else if (last) {
      start = endOf(last, tz) ?? { date: today, min: 0 };
    } else {
      // Nothing has started: the whole chain is the tail, from the head's own start.
      predecessor = null;
      const h = openTail[0];
      if (!h?.start_time) return null;
      start = pointOf(h.start_time, tz);
    }

    const tailBook = openTail.reduce((n, r) => n + (r.predicted_minutes ?? 0), 0);
    if (openTail.length === 0 && future === 0 && (!source || source.predicted === source.expected)) {
      return { status: 'noop' };
    }
    return replanTail(db, snap, {
      predecessor, tail: openTail,
      templateId: openTail.length ? undefined : (adjustable ?? last ?? lineage[0]).id,
      extraBook: future - tailBook, extraWork: 0, start,
      mayPullEarlier: future < tailBook || (!!source && source.predicted < (source.expected ?? 0)),
      // oct20d auditfix2 (CS): the started row's approved expectation follows the share confirmed here
      source, label, syncSourceShare: true,
      // oct20d auditfix3 (JH): a ledger-derived plan — the other started rows keep only the share it leaves them
      releaseHeldWork: true,
    });
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// LANE CASCADES — observe a chain row, never re-time it
// ═══════════════════════════════════════════════════════════════════════════

/** The fields a lane cascade already has for each of its tickets. */
export interface LaneTicket {
  id: string;
  status?: string | null;
  deleted_at?: string | null;
  actually_started?: boolean | null;
  timer_status?: string | null;
  rollover_of_ticket_id?: string | null;
  start_time: string;
  opti_finish?: string | null;
  opti_time?: number | null;
}

/**
 * Which of a lane's tickets does the chain planner govern? (Canonical
 * identity: unresolved AND a continuation, or a row a live row continues.)
 * One query for the lane's children; a failed read governs nothing new — the
 * continuation leg (rollover_of_ticket_id) needs no read at all.
 */
export async function laneChainGoverned(db: Db, lane: LaneTicket[], opts: { strict?: boolean } = {}): Promise<Set<string>> {
  const unresolved = lane.filter((t) => isUnresolvedChainRow({
    ...t, predicted_minutes: null, scheduled_date: null, assigned_tech_id: null,
  } as ReplanChainRow));
  const governed = new Set(unresolved.filter((t) => !!t.rollover_of_ticket_id).map((t) => t.id));
  const heads = unresolved.filter((t) => !t.rollover_of_ticket_id).map((t) => t.id);
  if (heads.length > 0) {
    const { data, error } = await db.from('tickets').select('rollover_of_ticket_id, status, deleted_at')
      .in('rollover_of_ticket_id', heads);
    // oct20d: `strict` (globalCascade) — an unreadable child list THROWS instead of governing nothing new.
    if (opts.strict && error) throw new Error(`chain children unreadable: ${error.message}`);
    for (const c of (data ?? []) as Array<{ rollover_of_ticket_id: string; status: string | null; deleted_at: string | null }>) {
      if (!c.deleted_at && c.status !== 'Deleted' && c.status !== 'Cancelled') governed.add(c.rollover_of_ticket_id);
    }
  }
  return governed;
}

/**
 * A governed row's CANONICAL window on its lane: where the chain planner put
 * it — its stored start to its stored OptiFinish (the planner writes both), or
 * start + its planned OptiTime when no finish is stored. Never start + a
 * placeholder or a book share re-derived by the lane.
 */
export function chainRowLaneWindow(
  t: LaneTicket, tz: string, breaks: Array<{ start: number; end: number }>,
  finishFromWork: (startMin: number, workMin: number, breaks: Array<{ start: number; end: number }>) => number,
): { startMin: number; finishMin: number } {
  const startMin = getMinutesInShopTZ(t.start_time, tz);
  if (t.opti_finish) {
    const f = pointOf(t.opti_finish, tz);
    const s = pointOf(t.start_time, tz);
    if (f.date === s.date && f.min >= startMin) return { startMin, finishMin: f.min };
  }
  if (t.opti_time && t.opti_time > 0) return { startMin, finishMin: finishFromWork(startMin, t.opti_time, breaks) };
  return { startMin, finishMin: startMin };
}

/**
 * A lane cascade found a governed chain row that must start later than it
 * does (work ahead of it on the lane now ends later). It asks the authority:
 * the row and its unresolved future are re-planned from that point — within
 * the day's remaining capacity, the rest onto the next valid days. Returns the
 * rows actually re-planned.
 */
export async function replanLaneChainRows(
  db: Db, requests: Array<{ id: string; date: string; min: number }>, label: string,
): Promise<number> {
  let written = 0;
  const done = new Set<string>();
  for (const r of requests) {
    if (done.has(r.id)) continue;
    done.add(r.id);
    const out = await replanFromRow(db, r.id, { date: r.date, min: r.min }, { label: `${label} ${r.id}` });
    if (out?.status === 'applied') written += out.written;
  }
  return written;
}

/**
 * Is this ticket a row of a multi-row rollover chain (by lineage)? Every
 * job-line writer asks this BEFORE writing a ticket's minutes: a chain row's
 * minutes are the chain authority's (applyChainWorkChange), never "this
 * ticket's job sum".
 */
export async function isRolloverChainTicket(db: Db, ticketId: string, opts: { strict?: boolean } = {}): Promise<boolean> {
  // oct20d: `strict` — a read error throws (loadChainSnapshotStrict) instead of answering "not a chain".
  const snap = opts.strict ? await loadChainSnapshotStrict(db, ticketId) : await loadChainSnapshot(db, ticketId);
  if (!snap) return false;
  const lineage = lineageFromHead(snap.row.parent_ticket_id ?? snap.row.id, snap.rows);
  return lineage.length > 1 && lineage.some((r) => r.id === snap.row.id);
}
